import { datadogLogs } from '@datadog/browser-logs';
import { FunctionalityEnum, UserRoleEnum } from '@elearning-platform/api-types/enums';
import { UserInfoInterface } from '@elearning-platform/api-types/interfaces';
import { Loader, UseState } from '@elearning-platform/ui';
import { ThemeProvider as MuiThemeProvider } from '@mui/material';
import { useQuery, UseQueryResult } from '@tanstack/react-query';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import React, { Dispatch, FC, LazyExoticComponent, ReactElement, Suspense, useEffect, useMemo, useReducer, useState } from 'react';
import ReactGA from 'react-ga4';
import { useDispatch, useSelector } from 'react-redux';
import { Location, Route, Routes, useLocation } from 'react-router-dom';
import { AnyAction } from 'redux';

import { getUserInfo, updateAssetsSession } from '../api/requests';
import { AppConfig } from '../appConfig';
import { selectAppSettingsBrands, selectCurrentBrand } from '../appSettings/redux/appSettings.selector';
import { CheckAuthLayout, VolatileSettingsProvider } from '../components';
import { AuthContext } from '../contexts';
import { AuthReducerAction, LocalStorageKey, QueryKey } from '../enums';
import { currentLanguage } from '../helpers/translations';
import { useLocalStorage } from '../hooks';
import { AppSettings, AuthContextState, RouteWithComponent } from '../interfaces';
import { authReducer } from '../reducers';
import { CommonActions, selectBrands } from '../redux';
import registerServiceWorker, { unregister } from '../registerServiceWorker';
import { getTheme } from '../theme';
import { AuthReducerActions, UseLocalStorage } from '../types/internal';
import CommonUserActions from '../userContext/commonUserActions';
import { getScrollMemoryPaths } from '../utils';

import { routes } from './app.routes';
import { AppLoadingScreen } from './appLoadingScreen/appLoadingScreen.component';
import { AppWrapper } from './appWrapper/appWrapper.component';
import { CookiePopup } from './cookiePopup/cookiePopup.component';
import { globalErrorHandler } from './errorHandlers';
import { LogoutErrorModal } from './logoutErrorModal/logoutErrorModal.component';
import { NotFoundPage } from './notFoundPage/notFoundPage.component';
import { RefreshPopup } from './refreshPopup/refreshPopup.component';
import { SocketProvider } from './socketProvider/socketProvider.component';

const App: FC = (): ReactElement => {
  const [theme, updateTheme] = useState(getTheme());
  const { pathname }: Location = useLocation();
  const [showRefreshPopup, setShowRefreshPopup]: UseState<boolean> = useState<boolean>(false);
  const [authState, authDispatch]: [AuthContextState, Dispatch<AuthReducerActions>] = useReducer(
    authReducer, { token: null, userData: null, appSettings: null, isAuthInitialized: false }
  );
  const dispatch: Dispatch<AnyAction> = useDispatch();
  const [userToken]: UseLocalStorage<string | null> = useLocalStorage<string | null>(LocalStorageKey.AuthorizationToken, null);
  const [appSettings, storeAppSettings]: UseLocalStorage<AppSettings | null> = useLocalStorage<AppSettings | null>(
    LocalStorageKey.AppSettings, null
  );
  const scrollMemoryPaths: string[] = useMemo((): string[] => getScrollMemoryPaths(), []);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const settingsBrands: any = useSelector(selectAppSettingsBrands);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const commonBrands: any = useSelector(selectBrands);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const currentBrandId: any = useSelector(selectCurrentBrand);

  const { data: userData, isLoading: isUserDataLoading }: UseQueryResult<UserInfoInterface> = useQuery({
    queryKey: [QueryKey.UserInfo],
    queryFn: (): Promise<UserInfoInterface> => getUserInfo(),
    enabled: !!userToken.value,
    staleTime: 1_000,
  });

  useEffect(
    (): void => {
      if (AppConfig.logsSystemClientToken) {
        datadogLogs.init({
          clientToken: AppConfig.logsSystemClientToken,
          site: 'datadoghq.eu',
          service: AppConfig.platformName,
          forwardErrorsToLogs: true,
          sampleRate: 100,
        });
      }
      globalErrorHandler();

      if (AppConfig.GA_id) ReactGA.initialize(AppConfig.GA_id);
      dayjs.locale(currentLanguage);
      dayjs.extend(relativeTime);
    },
    []
  );

  useEffect((): void => {
    if (!scrollMemoryPaths.includes(pathname)) {
      window.scrollTo(0, 0);
    }
  }, [pathname, scrollMemoryPaths]);

  useEffect((): void => {
    const userAgentString: string = navigator.userAgent;
    const chromeAgent: boolean = userAgentString.indexOf('Chrome') > -1;
    let safariAgent: boolean = userAgentString.indexOf('Safari') > -1;
    if (chromeAgent && safariAgent) {
      safariAgent = false;
    }

    if (safariAgent) {
      unregister();
    } else {
      registerServiceWorker({
        onUpdate: (registration: ServiceWorkerRegistration): void => {
          registration.waiting?.postMessage({ type: 'SKIP_WAITING' });
          setShowRefreshPopup(true);
        },
      });
    }
  }, []);

  useEffect((): void => {
    dispatch(CommonActions.fetchBrands());
  }, [dispatch]);

  useEffect(
    (): void => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const brand: any = settingsBrands.concat(commonBrands).find(({ id }: any): boolean => id === currentBrandId);
      updateTheme(getTheme(brand));
    },
    [currentBrandId, settingsBrands, commonBrands]
  );

  useEffect(
    (): void => {
      if (userToken.value) {
        void updateAssetsSession();
      }
    },
    [userToken.value]
  );

  useEffect(
    (): void => {
      if (isUserDataLoading || appSettings.isLoading) {
        return;
      }
      if (!authState.isAuthInitialized) {
        authDispatch({
          type: AuthReducerAction.Init,
          payload: {
            token: userToken.value,
            userData: userData ?? null,
            appSettings: appSettings.value ?? { currentMenuRole: UserRoleEnum.USER },
          },
        });
      }
    },
    [appSettings.value, userData, authDispatch, appSettings.isLoading, isUserDataLoading, authState.isAuthInitialized, userToken.value]
  );

  useEffect(
    (): void => {
      if (isUserDataLoading) {
        return;
      }
      if (authState.isAuthInitialized) {
        authDispatch({ type: AuthReducerAction.UpdateUserInfo, payload: userData ?? null });
      }
    },
    [userData, authDispatch, isUserDataLoading, authState.isAuthInitialized]
  );

  useEffect(
    (): void => {
      if (authState.userData?.availableRoutes[FunctionalityEnum.ApiNotificationList]) {
        dispatch(CommonActions.fetchNotifications());
      }
    },
    [authState.userData?.availableRoutes, dispatch]
  );

  useEffect(
    (): void => {
      if (!authState.appSettings) {
        return;
      }

      storeAppSettings(authState.appSettings);
    },
    [authState.appSettings, storeAppSettings]
  );

  useEffect(
    (): void => {
      if (!appSettings.value || !!authState.appSettings) {
        return;
      }

      authDispatch({ type: AuthReducerAction.UpdateAppSettings, payload: appSettings.value });
    },
    [appSettings.value, authState.appSettings, authDispatch]
  );

  return (
    <VolatileSettingsProvider>
      <AuthContext.Provider value={[authState, authDispatch]}>
        <SocketProvider>
          <MuiThemeProvider theme={theme}>
            {!!showRefreshPopup && <RefreshPopup />}
            <AppLoadingScreen />
            <CookiePopup />
            <AppWrapper>
              <Suspense fallback={<Loader />}>
                <Routes>
                  {routes.flatMap((route: RouteWithComponent): JSX.Element[] => {
                    if ('children' in route.pathSettings) {
                      return Object.values(route.pathSettings.urls).map((url: string, index: number): JSX.Element => {
                        const Component: LazyExoticComponent<FC> = route.component;

                        return (
                          <Route
                            element={<Component />}
                            // eslint-disable-next-line react/no-array-index-key
                            key={`${url}-${index}`}
                            path={`${url}/*`}
                          />
                        );
                      });
                    } else {
                      return Object.values(route.pathSettings.urls).map((url: string, index: number): JSX.Element => {
                        const Component: LazyExoticComponent<FC> = route.component;

                        return (
                          <Route
                            element={<CheckAuthLayout />}
                            // eslint-disable-next-line react/no-array-index-key
                            key={`${url}-${index}`}
                          >
                            <Route
                              element={<Component />}
                              path={url}
                            />
                          </Route>
                        );
                      });
                    }
                  })}
                  <Route element={<NotFoundPage />} path="*" />
                </Routes>
              </Suspense>
              <CommonUserActions />
            </AppWrapper>
            <LogoutErrorModal />
          </MuiThemeProvider>
        </SocketProvider>
      </AuthContext.Provider>
    </VolatileSettingsProvider>
  );
};

export default App;
